import { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';

import { DashboardContent } from 'src/layouts/dashboard';

import { Iconify } from 'src/components/iconify';
import { ReportScopeFilter } from 'src/components/report-filter/ReportScopeFilter';
import { useReportScope } from 'src/routes/context/ReportScopeContext';

import { downloadBlob } from 'src/utils/download-file';

import {
  getMonthlyAttendance,
  exportMonthlyAttendance,
  type DayStatus,
  type ExportFormat,
  type MonthlyAttendanceMatrix,
} from 'src/api/reports.api';

const MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const STATUS_STYLE: Record<DayStatus, { bg: string; label: string; color: string }> = {
  PRESENT: { bg: 'success.lighter', color: 'success.darker', label: '' },
  ABSENT: { bg: 'error.lighter', color: 'error.darker', label: 'Yok' },
  LEAVE: { bg: 'info.lighter', color: 'info.darker', label: 'İzin' },
  OFF: { bg: 'grey.200', color: 'text.disabled', label: '·' },
};

function fmtHm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}s ${m}d`;
}

function MonthlyAttendanceInner() {
  const { companyId, branchId } = useReportScope();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MonthlyAttendanceMatrix | null>(null);

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);

  const handleFetch = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await getMonthlyAttendance(companyId, branchId, year, month);
      setData(res);
    } catch (e) {
      setError('Rapor yüklenemedi. Yetki veya bağlantı sorununu kontrol edin.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const handleExport = async (format: ExportFormat) => {
    setExporting(format);
    try {
      const blob = await exportMonthlyAttendance(companyId, branchId, year, month, format);
      downloadBlob(blob, `aylik-devam-${year}-${String(month).padStart(2, '0')}`, format);
    } catch (e) {
      setError('Dışa aktarma başarısız oldu.');
    } finally {
      setExporting(null);
    }
  };

  const dayNums = data ? Array.from({ length: data.daysInMonth }, (_, i) => i + 1) : [];

  return (
    <DashboardContent>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4">Aylık Devam Raporu</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Şube ve ay seçerek çalışan × gün devam matrisini görüntüleyin.
        </Typography>
      </Box>

      <Card sx={{ p: 3, mb: 3 }}>
        <ReportScopeFilter />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Yıl</InputLabel>
            <Select value={year} label="Yıl" onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Ay</InputLabel>
            <Select value={month} label="Ay" onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => (
                <MenuItem key={m} value={i + 1}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleFetch} disabled={loading}>
            {loading ? 'Yükleniyor…' : 'Raporu Getir'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="error"
            startIcon={<Iconify icon="solar:file-text-bold" />}
            disabled={!data || exporting !== null}
            onClick={() => handleExport('PDF')}
          >
            {exporting === 'PDF' ? 'PDF…' : 'PDF'}
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<Iconify icon="solar:file-download-bold" />}
            disabled={!data || exporting !== null}
            onClick={() => handleExport('EXCEL')}
          >
            {exporting === 'EXCEL' ? 'Excel…' : 'Excel'}
          </Button>
        </Stack>
        {error && (
          <Typography variant="body2" sx={{ color: 'error.main', mt: 2 }}>{error}</Typography>
        )}
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && !loading && (
        <Card sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {data.branchName} — {MONTHS[data.month - 1]} {data.year}
          </Typography>
          <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
            <Table size="small" stickyHeader sx={{ '& td, & th': { px: 0.75, py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper',
                      minWidth: 180, fontWeight: 600,
                    }}
                  >
                    Çalışan
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Şube</TableCell>
                  {dayNums.map((d) => (
                    <TableCell key={d} align="center" sx={{ minWidth: 30, fontSize: 12 }}>{d}</TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 600, minWidth: 70 }}>Toplam</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((row) => (
                  <TableRow key={row.employeeId} hover>
                    <TableCell
                      sx={{
                        position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper',
                        fontWeight: 500,
                      }}
                    >
                      {row.fullName}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{row.branchName}</TableCell>
                    {row.days.map((cell) => {
                      const st = STATUS_STYLE[cell.dayStatus];
                      const tip = cell.dayStatus === 'PRESENT'
                        ? `${cell.checkIn ?? '—'} → ${cell.checkOut ?? '—'} (${fmtHm(cell.workMinutes)})`
                        : cell.dayStatus;
                      return (
                        <Tooltip key={cell.date} title={tip} arrow>
                          <TableCell
                            align="center"
                            sx={{ bgcolor: st.bg, color: st.color, fontSize: 11, cursor: 'default' }}
                          >
                            {cell.dayStatus === 'PRESENT'
                              ? (cell.checkIn ? cell.checkIn.slice(0, 5) : '')
                              : st.label}
                          </TableCell>
                        </Tooltip>
                      );
                    })}
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{fmtHm(row.totalMinutes)}</TableCell>
                  </TableRow>
                ))}
                {data.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={dayNums.length + 3} align="center" sx={{ py: 4 }}>
                      Bu kapsam/ay için kayıt bulunamadı.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </DashboardContent>
  );
}

export function MonthlyAttendanceView() {
  return <MonthlyAttendanceInner />;
}
